<script setup lang="ts">
import { ref, computed } from 'vue';

interface Flashcard {
  question: string;
  answer: string;
}

const cards: Flashcard[] = [
  {
    question: 'What is the forgetting curve?',
    answer: 'Memory retention drops exponentially over time without review—to ~20% within a month.',
  },
  {
    question: 'What is spaced repetition?',
    answer: 'A learning technique that reviews information at increasing intervals to maximize retention.',
  },
  {
    question: 'What is the testing effect?',
    answer: 'Actively recalling information strengthens memory more than passive re-reading.',
  },
];

const currentCardIndex = ref(0);
const isRevealed = ref(false);
const interval = ref(1); // days until next review
const showIntervalChange = ref(false);
const intervalDelta = ref(0);
const selectedRating = ref<'again' | 'hard' | 'good' | 'easy' | null>(null);

const currentCard = computed(() => cards[currentCardIndex.value]);

function reveal() {
  isRevealed.value = true;
}

function rate(rating: 'again' | 'hard' | 'good' | 'easy') {
  if (selectedRating.value) return; // Prevent double-clicks

  selectedRating.value = rating;
  let newInterval: number;

  switch (rating) {
    case 'again':
      newInterval = 1;
      break;
    case 'hard':
      // Keep decimal for small values so change is visible
      newInterval = Math.max(1, Math.round(interval.value * 1.2 * 10) / 10);
      break;
    case 'good':
      newInterval = Math.round(interval.value * 2.5 * 10) / 10;
      break;
    case 'easy':
      newInterval = Math.round(interval.value * 4 * 10) / 10;
      break;
  }

  intervalDelta.value = newInterval - interval.value;
  interval.value = newInterval;
  showIntervalChange.value = true;

  // Reset and advance to next card after a moment
  setTimeout(() => {
    showIntervalChange.value = false;
    isRevealed.value = false;
    selectedRating.value = null;
    currentCardIndex.value = (currentCardIndex.value + 1) % cards.length;
  }, 900);
}

function formatInterval(days: number): string {
  if (days < 7) {
    // Show one decimal for small values so changes are visible
    const formatted = days % 1 === 0 ? days.toString() : days.toFixed(1);
    return `${formatted} ${days === 1 ? 'day' : 'days'}`;
  }
  if (days < 30) {
    const weeks = Math.round(days / 7);
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  }
  const months = Math.round(days / 30);
  return months === 1 ? '1 month' : `${months} months`;
}
</script>

<template>
  <div class="flex flex-col md:flex-row gap-6 items-center justify-center">
    <!-- Flashcard -->
    <div class="w-full max-w-md">
      <div
        class="rounded-lg border border-border bg-card shadow-md overflow-hidden"
      >
        <!-- Card content -->
        <div class="p-6 min-h-[200px] flex flex-col">
          <!-- Question -->
          <div class="mb-4">
            <span class="text-xs text-muted-foreground uppercase tracking-wide">Question</span>
            <p class="text-foreground mt-1">{{ currentCard.question }}</p>
          </div>

          <!-- Answer (revealed) -->
          <Transition name="fade">
            <div v-if="isRevealed" class="mb-4">
              <span class="text-xs text-muted-foreground uppercase tracking-wide">Answer</span>
              <p class="text-foreground mt-1">{{ currentCard.answer }}</p>
            </div>
          </Transition>

          <!-- Actions -->
          <div class="mt-auto pt-4">
            <Transition name="fade" mode="out-in">
              <!-- Show answer button -->
              <button
                v-if="!isRevealed"
                class="w-full py-2 px-4 rounded-md bg-muted hover:bg-muted/80 text-foreground text-sm font-medium transition cursor-pointer"
                @click="reveal"
              >
                Show Answer
              </button>

              <!-- Rating buttons -->
              <div v-else class="flex gap-2">
                <button
                  class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition cursor-pointer"
                  :class="
                    selectedRating === 'again'
                      ? 'bg-red-500/30 ring-2 ring-red-500 text-red-700 dark:text-red-300'
                      : 'bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400'
                  "
                  @click="rate('again')"
                >
                  Again
                </button>
                <button
                  class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition cursor-pointer"
                  :class="
                    selectedRating === 'hard'
                      ? 'bg-orange-500/30 ring-2 ring-orange-500 text-orange-700 dark:text-orange-300'
                      : 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-600 dark:text-orange-400'
                  "
                  @click="rate('hard')"
                >
                  Hard
                </button>
                <button
                  class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition cursor-pointer"
                  :class="
                    selectedRating === 'good'
                      ? 'bg-green-500/30 ring-2 ring-green-500 text-green-700 dark:text-green-300'
                      : 'bg-green-500/10 hover:bg-green-500/20 text-green-600 dark:text-green-400'
                  "
                  @click="rate('good')"
                >
                  Good
                </button>
                <button
                  class="flex-1 py-2 px-3 rounded-md text-sm font-medium transition cursor-pointer"
                  :class="
                    selectedRating === 'easy'
                      ? 'bg-blue-500/30 ring-2 ring-blue-500 text-blue-700 dark:text-blue-300'
                      : 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400'
                  "
                  @click="rate('easy')"
                >
                  Easy
                </button>
              </div>
            </Transition>
          </div>
        </div>
      </div>
    </div>

    <!-- Interval display -->
    <div class="flex flex-col items-center gap-2 min-w-[140px]">
      <span class="text-sm text-muted-foreground">Next review in</span>
      <div class="relative">
        <span class="text-3xl font-bold text-foreground">
          {{ formatInterval(interval) }}
        </span>
        <!-- Delta indicator -->
        <Transition name="pop">
          <span
            v-if="showIntervalChange && intervalDelta !== 0"
            class="absolute -right-8 top-0 text-sm font-medium"
            :class="intervalDelta > 0 ? 'text-green-500' : 'text-red-500'"
          >
            {{ intervalDelta > 0 ? '+' : '' }}{{ intervalDelta % 1 === 0 ? intervalDelta : intervalDelta.toFixed(1) }}
          </span>
        </Transition>
      </div>
      <p class="text-xs text-muted-foreground text-center max-w-[160px]">
        Better recall means longer intervals
      </p>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.pop-enter-active {
  transition: all 0.3s ease-out;
}
.pop-leave-active {
  transition: all 0.2s ease-in;
}
.pop-enter-from {
  opacity: 0;
  transform: translateY(10px) scale(0.9);
}
.pop-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
